import { memo } from 'react';
import {
  ChakraFlex,
  Avatar,
  Tag,
  TagColor,
  Text,
  Icon,
  FAIconName,
} from '@equip.health/ui';
import { getPreferredFullName } from '~/lib/util/patient.util';
import util from '~/lib/util/util';
import BadgeIcon from './BadgeIcon';

export interface AvatarUser {
  userId?: string;
  firstName?: string;
  lastName?: string;
  chosenName?: string;
  pronouns?: string | string[];
  role?: string;
  providerTypeName?: string;
  providerTypeDetails?: { name?: string; externalId?: string };
  avatar?: { thumbnailUrl?: string } | null;
  // TODO the keys below seem highly duplicative with the avatar object
  // which is missing props from the GetProviderByIdResponse avatar object
  avatarThumbnailUrl?: string;
  avatarUrl?: string;
  pronunciation?: string;
  timeZoneDetails?: { code?: string } | null;
  timezone?: string;
  credentialsDetails?: { name?: string }[];
  relationship?: string;
  primaryTag?: string;
}

export interface SelectableUserAvatarBlockProps {
  id: string;
  isSelected?: boolean;
  user: AvatarUser;
  variant?: 'standard' | 'compact' | 'attendeeCard';
  showPronouns?: boolean;
  showPronunciation?: boolean;
  showTimezone?: boolean;
  showPrimaryTag?: boolean;
  roleDisplay?: string;
  avatarSize?: 'sm' | 'md' | 'lg';
  onToggle?: (id: string) => void;
}

const variantStyles = {
  attendeeCard: {
    borderRadius: '12px',
    borderStyle: 'solid',
    borderWidth: '1px',
    gap: '12px',
    minHeight: '77px',
    padding: '18px',
  },
  compact: { gap: '12px', padding: '12px' },
  standard: { gap: '18px', padding: '18px' },
} as const;

const getDisplayName = (user: AvatarUser) =>
  getPreferredFullName(
    user.chosenName ?? '',
    user.firstName ?? '',
    user.lastName ?? '',
    false,
  );

const getRoleDisplay = (user: AvatarUser) => {
  return util.capitalize(
    user.relationship ||
      user.providerTypeDetails?.name ||
      user.providerTypeName ||
      user.role ||
      'User',
  );
};

const getAvatarUserType = (role?: string) => {
  if (role === 'patient') return 'patient' as const;
  if (role === 'support') return 'support' as const;
  return 'provider' as const;
};

const getPronouns = (pronouns?: string | string[]) =>
  pronouns && (Array.isArray(pronouns) ? pronouns.join(', ') : pronouns);

const getAvatarCol = (avatarSize?: 'sm' | 'md' | 'lg') => {
  if (avatarSize === 'sm') return '40px';
  if (avatarSize === 'lg') return '56px';
  return '48px';
};

const getAttendeeFlexProps = ({
  id,
  isSelected,
  onToggle,
}: {
  id: string;
  isSelected: boolean;
  onToggle?: (id: string) => void;
}) => ({
  _focusVisible: {
    boxShadow: '0 0 0 3px var(--chakra-colors-primary-30)',
    outline: 'none',
  },
  _hover: {
    backgroundColor: 'primary.10',
    borderColor: 'primary.100',
  },
  alignItems: 'center',
  'aria-selected': isSelected,
  backgroundColor: isSelected ? 'primary.10' : 'white',
  borderColor: isSelected ? 'primary.100' : 'neutral.line',
  borderRadius: '12px',
  borderStyle: 'solid',
  borderWidth: '1px',
  cursor: 'pointer',
  'data-testid': `${id}__attendee-card`,
  gap: '12px',
  minHeight: '77px',
  onClick: typeof onToggle === 'function' ? () => onToggle(id) : undefined,
  onKeyDown:
    typeof onToggle === 'function'
      ? (e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle(id);
          }
        }
      : undefined,
  padding: '18px',
  role: 'option',
  tabIndex: 0,
  width: '100%',
});

function AttendeeContent({
  id,
  displayName,
  pronouns,
  roleDisplay,
}: {
  id: string;
  displayName: string;
  pronouns?: string | false | null;
  roleDisplay: string;
}) {
  return (
    <ChakraFlex flexDirection="column" minWidth={0} overflow="hidden">
      <ChakraFlex alignItems="center" gap="6px" minWidth={0}>
        <Text
          id={`${id}__name`}
          noOfLines={1}
          style={{ fontSize: '15px', lineHeight: '140%' }}
          title={displayName}
          variant="bodyMedium"
        >
          {displayName}
        </Text>
        {!!pronouns && (
          <Text
            color="neutral.secondary"
            id={`${id}__pronouns`}
            style={{ fontSize: '13px', lineHeight: '140%' }}
            variant="caption"
          >
            ({pronouns})
          </Text>
        )}
      </ChakraFlex>
      <Text
        color="neutral.secondary"
        id={`${id}__role`}
        noOfLines={1}
        style={{ fontSize: '13px', lineHeight: '140%' }}
        title={roleDisplay}
        variant="caption"
      >
        {roleDisplay}
      </Text>
    </ChakraFlex>
  );
}

function RegularContent({
  id,
  displayName,
  pronouns,
  roleDisplay,
  postfix,
  showPronouns,
  primaryTag,
  showPrimaryTag,
}: {
  id: string;
  displayName: string;
  pronouns?: string | false | null;
  roleDisplay?: string;
  postfix: string;
  showPronouns?: boolean;
  primaryTag?: string;
  showPrimaryTag?: boolean;
}) {
  const hasPrimaryTag = showPrimaryTag && primaryTag;
  return (
    <ChakraFlex
      alignItems="flex-start"
      flexDirection="column"
      gap="6px"
      minWidth={0}
      width="100%"
    >
      <ChakraFlex alignItems="center" gap="6px" minWidth={0}>
        <Text
          color="neutral.primary"
          display="inline-block"
          id={`${id}__name${postfix}`}
          noOfLines={1}
          title={displayName}
          variant="bodyMedium"
        >
          {displayName}
        </Text>
        {showPronouns && pronouns && (
          <Text
            color="neutral.secondary"
            display="inline"
            id={`${id}__pronouns${postfix}`}
            sx={{ whiteSpace: 'nowrap' }}
            variant="caption"
          >
            ({pronouns})
          </Text>
        )}
      </ChakraFlex>
      {roleDisplay && (
        <ChakraFlex
          alignItems="center"
          flexDirection="row"
          gap="6px"
          minWidth={0}
        >
          <Text
            color="neutral.secondary"
            id={`${id}__role${postfix}`}
            variant="caption"
          >
            {roleDisplay}
          </Text>
        </ChakraFlex>
      )}
      {hasPrimaryTag && (
        <ChakraFlex alignItems="center" gap="6px" minWidth={0}>
          <BadgeIcon />
          <Text color="accent.blue" variant="caption">
            Clinical Concentration Area: {primaryTag}
          </Text>
        </ChakraFlex>
      )}
    </ChakraFlex>
  );
}

function BottomFull({
  id,
  isSelected,
  postfix,
  user,
  showPrimaryTag,
  primaryTag,
  showPronunciation,
  showTimezone,
  timezone,
  variant,
}: {
  id: string;
  isSelected: boolean;
  postfix: string;
  user: AvatarUser;
  primaryTag?: string;
  showPrimaryTag?: boolean;
  showPronunciation?: boolean;
  showTimezone?: boolean;
  timezone?: string;
  variant: 'standard' | 'compact' | 'attendeeCard';
}) {
  if (!showPronunciation && !showTimezone) return null;
  const hasTimezone =
    showTimezone && (user?.timeZoneDetails?.code ?? user?.timezone);
  const hasPrimaryTag = showPrimaryTag && primaryTag;

  return (
    <ChakraFlex
      flexDirection={variant === 'compact' ? 'column' : 'row'}
      gap="8px"
      gridColumn="1 / -1"
      gridRow="2"
      id={`${id}__bottomFull${postfix}`}
      minWidth={0}
      {...(variant !== 'compact' && {
        alignItems: 'center',
        alignSelf: 'center',
        flex: '1 0 auto',
        gap: '18px',
        justifyContent: 'flex-end',
      })}
    >
      {showPronunciation && user.pronunciation && (
        <Text
          color="neutral.secondary"
          data-testid={`${id}__pronunciation${postfix}`}
          id={`${id}__pronunciation${postfix}`}
          noOfLines={1}
          sx={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          title={user.pronunciation}
          variant="caption"
        >
          {user.pronunciation}
        </Text>
      )}

      {hasPrimaryTag && (
        <ChakraFlex
          alignItems="center"
          flexDirection="row"
          gap="4px"
          marginBottom="6px"
          minWidth={0}
        >
          <BadgeIcon />
          <Text color="accent.blue" variant="caption">
            Clinical Concentration Area: {primaryTag}
          </Text>
        </ChakraFlex>
      )}
      {hasTimezone && (
        <Tag
          color={TagColor.white}
          id={`${id}__timezone${postfix}`}
          isBorderRequired
          maxWidth="fit-content"
          size="sm"
          value={timezone}
        />
      )}
      {isSelected && (
        <Icon color="primary.100" name={FAIconName.checkRegular} />
      )}
    </ChakraFlex>
  );
}

function AttendeeBlock({
  id,
  displayName,
  pronouns,
  roleDisplay,
  avatarUserType,
  isSelected,
  onToggle,
  src,
  primaryTag,
}: {
  id: string;
  displayName: string;
  pronouns?: string | false | null;
  roleDisplay: string;
  avatarUserType: 'patient' | 'support' | 'provider';
  isSelected: boolean;
  onToggle?: (id: string) => void;
  src?: string;
  primaryTag?: string;
}) {
  const props = getAttendeeFlexProps({ id, isSelected, onToggle });
  return (
    <ChakraFlex {...props}>
      <ChakraFlex position="relative">
        <Avatar
          aria-label={`avatar-${displayName}`}
          id={`${id}__avatar`}
          name={displayName}
          size="sm"
          src={src}
          userType={avatarUserType}
        />
        {primaryTag && (
          <ChakraFlex position="absolute" right="-6px" top="-6px">
            <BadgeIcon withBackground />
          </ChakraFlex>
        )}
      </ChakraFlex>

      <ChakraFlex
        alignItems="center"
        gap="12px"
        justifyContent="space-between"
        minWidth={0}
        width="100%"
      >
        <AttendeeContent
          displayName={displayName}
          id={id}
          pronouns={pronouns}
          roleDisplay={roleDisplay}
        />
        {isSelected && (
          <Icon color="primary.100" name={FAIconName.checkRegular} />
        )}
      </ChakraFlex>
    </ChakraFlex>
  );
}

function RegularBlock({
  id,
  postfix,
  displayName,
  pronouns,
  roleDisplay,
  avatarUserType,
  avatarSize,
  avatarCol,
  variant,
  user,
  showPronunciation,
  showTimezone,
  showPrimaryTag,
  timezone,
  showPronouns,
  isSelected,
}: {
  id: string;
  postfix: string;
  displayName: string;
  pronouns?: string | false | null;
  roleDisplay?: string;
  avatarUserType: 'patient' | 'support' | 'provider';
  avatarSize: 'sm' | 'md' | 'lg';
  avatarCol: string;
  variant: 'standard' | 'compact' | 'attendeeCard';
  user: AvatarUser;
  showPronunciation?: boolean;
  showTimezone?: boolean;
  showPrimaryTag?: boolean;
  timezone?: string;
  showPronouns?: boolean;
  isSelected: boolean;
}) {
  const gridProps = {
    alignItems: 'flex-start',
    display: variant === 'compact' ? 'grid' : 'flex',
    id: `${id}${postfix}`,
    justifyContent: 'flex-start',
    minWidth: 0,
    padding: variantStyles[variant].padding,
    sx: {
      columnGap: variantStyles[variant].gap,
      gridTemplateColumns: `${avatarCol} 1fr`,
      rowGap: '8px',
    },
    width: '100%',
  } as const;

  return (
    <ChakraFlex {...gridProps} overflow="hidden">
      <ChakraFlex position="relative">
        <Avatar
          aria-label={`avatar-${displayName}`}
          id={`${id}__avatar`}
          name={displayName}
          size={avatarSize}
          src={
            user.avatar?.thumbnailUrl ??
            user.avatarThumbnailUrl ??
            user.avatarUrl
          }
          userType={avatarUserType}
        />
      </ChakraFlex>

      <ChakraFlex
        alignItems="flex-start"
        direction="column"
        gap="6px"
        gridColumn="2"
        gridRow="1"
        id={`${id}__rightTop${postfix}`}
        minWidth={0}
        overflow="hidden"
      >
        <RegularContent
          displayName={displayName}
          id={id}
          postfix={postfix}
          pronouns={pronouns}
          roleDisplay={roleDisplay}
          showPronouns={showPronouns}
          primaryTag={user.primaryTag}
          showPrimaryTag={variant === 'standard' ? showPrimaryTag : false}
        />
      </ChakraFlex>

      <BottomFull
        id={id}
        isSelected={!!isSelected}
        postfix={postfix}
        primaryTag={user.primaryTag}
        showPrimaryTag={variant === 'compact' ? showPrimaryTag : false}
        showPronunciation={showPronunciation}
        showTimezone={showTimezone}
        timezone={timezone}
        user={user}
        variant={variant}
      />
    </ChakraFlex>
  );
}

const SelectableUserAvatarBlockInner = ({
  id,
  user,
  isSelected = false,
  variant = 'standard',
  showPronouns = true,
  showPronunciation = true,
  showTimezone = true,
  showPrimaryTag = true,
  roleDisplay: roleDisplayOverride,
  avatarSize = 'md',
  onToggle,
}: SelectableUserAvatarBlockProps) => {
  const postfix = `--user-id-${user.userId ?? 'unknown'}`;
  const timezone = user?.timeZoneDetails?.code ?? user?.timezone;

  const displayName = getDisplayName(user);
  const roleDisplay = roleDisplayOverride ?? getRoleDisplay(user);
  const avatarUserType = getAvatarUserType(user.role);
  const pronouns = getPronouns(user.pronouns);
  const avatarCol = getAvatarCol(avatarSize);

  if (variant === 'attendeeCard') {
    return (
      <AttendeeBlock
        avatarUserType={avatarUserType}
        displayName={displayName}
        id={id}
        isSelected={!!isSelected}
        onToggle={onToggle}
        primaryTag={user.primaryTag}
        pronouns={pronouns}
        roleDisplay={roleDisplay}
        src={
          user.avatar?.thumbnailUrl ?? user.avatarThumbnailUrl ?? user.avatarUrl
        }
      />
    );
  }

  return (
    <RegularBlock
      avatarCol={avatarCol}
      avatarSize={avatarSize}
      avatarUserType={avatarUserType}
      displayName={displayName}
      id={id}
      isSelected={!!isSelected}
      postfix={postfix}
      pronouns={pronouns}
      roleDisplay={roleDisplay}
      showPrimaryTag={showPrimaryTag}
      showPronouns={showPronouns}
      showPronunciation={showPronunciation}
      showTimezone={showTimezone}
      timezone={timezone}
      user={user}
      variant={variant}
    />
  );
};

const SelectableUserAvatarBlock = memo(SelectableUserAvatarBlockInner);
SelectableUserAvatarBlock.displayName = 'SelectableUserAvatarBlock';
export default SelectableUserAvatarBlock;
